import type { DistrictMode } from '@citizen-walk/shared-types';

/**
 * Sprint AI (2026-05-19): map 初期表示パラメータ算出 helpers。
 *
 * 旧 MapTab L592-597 zoomForRadius 同等。M5 displayRules.mapDefaults 連動の
 * full 移植 (admin master 駆動) は schema が `displayRules: unknown` のままなので
 * 後続 sprint で具体化、本 commit は District.mode + postAreaRadiusM 駆動の minimum。
 */

/**
 * 指定半径 (meters) が viewport に収まるような zoom level を算出。
 * 旧 zoomForRadius(radiusM, latDeg, viewportPx=600) 踏襲、丸め 19 max / 1 min。
 *
 * 参考: zoom z で 1px = 156543.03 * cos(lat) / 2^z [m] (Web Mercator)。
 */
export function zoomForRadius(radiusM: number, latDeg: number, viewportPx = 600): number {
  const earthCircumference = 40075000 * Math.cos((latDeg * Math.PI) / 180);
  const metersPerPixel = (2 * radiusM) / viewportPx;
  const zoom = Math.log2(earthCircumference / (metersPerPixel * 512));
  return Math.max(1, Math.min(19, zoom));
}

/**
 * District.mode 別の marker デフォルト色 (旧 modeTemplates.ts の colorRule 簡略版)。
 * M5 displayRules で admin が上書き設定するまでの fallback。
 *
 * 【廃止予定 fallback】mode 文字列分岐は組込4モード (legacy) のみ (モードドリブン化 MD-4、
 * 2026-07-24)。custom mode は default (blue) に落ちる。本命は District.mapDisplay.colorRule /
 * M5 displayRules.map — 設定済み地区はこの fallback に到達しない。撤去条件: 全地区の colorRule 設定。
 *
 * - ecw: 感情色相 (旧 EcwInsight sentiment 由来) の代わりに primary blue
 * - walker: 緑系 (歩行アンケート、旧 walker mode の活気イメージ)
 * - bousai: 暖色赤 (危険箇所、旧 bousai mode の警告イメージ)
 * - kids-bousai: amber (子供視点防災)
 * - legacy / その他: blue-500 (default)
 */
export function defaultMarkerColorForMode(mode: DistrictMode | null | undefined): string {
  switch (mode) {
    case 'ecw':
      return '#3b82f6'; // blue-500
    case 'walker':
      return '#10b981'; // emerald-500
    case 'bousai':
      return '#ef4444'; // red-500
    case 'kids-bousai':
      return '#f59e0b'; // amber-500
    default:
      return '#3b82f6';
  }
}

/**
 * Sprint AR (2026-05-20): District.mode 別の marker / 凡例ラベル。
 *
 * 【廃止予定 fallback】mode 文字列分岐は組込4モード (legacy) のみ (モードドリブン化 MD-4、
 * 2026-07-24)。custom mode は null (凡例 entry なし)。M5 displayRules 駆動化で置換予定。
 *
 * MapLegend で `defaultMarkerColorForMode(mode)` と組み合わせて
 * 「色丸 + ラベル」の 1 entry を生成するために使用。
 *
 * 旧 MapTab の getQuadrantLegendEntries (mapDisplay.colorRule 駆動) は
 * displayRules schema 未確定 (`displayRules: unknown`) で full 移植不可。
 * M5 displayRules.mapDefaults が具体型化されたら quadrant 等の多色 entries に
 * 拡張し、ここは「fallback ラベル」専用となる予定。
 *
 * mode 未確定 (null/undefined) 時は null。MapLegend 側で「entry なし → button 非表示」。
 */
export function mapModeLabel(mode: DistrictMode | null | undefined): string | null {
  switch (mode) {
    case 'ecw':
      return '投稿位置';
    case 'walker':
      return '歩行投稿';
    case 'bousai':
      return '気付き箇所';
    case 'kids-bousai':
      return '子供視点';
    default:
      return null;
  }
}
